import { CSSProperties, useRef, useState, useEffect, startTransition } from 'react';
import { Header, flexRender } from '@tanstack/react-table'; // Header 타입 추가
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/utils/tailwind';
import SortDefaultIcon from '@/assets/icons/OrderDetails/eva_repeat-fill.svg?react';
import SortAscIcon from '@/assets/icons/SortAscIcon.svg?react';
import SortDescIcon from '@/assets/icons/SortDescIcon.svg?react';
import { useSort } from '@/contexts/table-sort';
import { TABLE_CHECKBOX_COLUMN_ID, TABLE_CHECKBOX_HEADER_Z_INDEX } from './tableConstants';

interface DraggableTableHeaderProps<TData extends object> {
  header: Header<TData, unknown>; // TanStack Table Header 타입
  disableDrag?: boolean;
  enableResize?: boolean; // 컬럼 너비 조절 핸들 표시 여부
  onMeasureMin?: (columnId: string, minWidth: number) => void; // 헤더 실측 최소너비 보고
}

interface SortingButtonProps<TData extends object> {
  header: Header<TData, unknown>; // DraggableTableHeaderProps에서 사용한 것과 동일한 타입
  charCount: number;
}

type SortColumnId = 'orderDate' | 'salesAmount' | 'quantity';

const SORT_COLUMN: Record<string, 'OrderDate' | 'SalesAmount' | 'OrderCount'> = {
  orderDate: 'OrderDate',
  salesAmount: 'SalesAmount',
  quantity: 'OrderCount',
};

// 정렬 아이콘(16px)의 left 오프셋. 헤더 텍스트(12px 폰트) 바로 뒤에 붙는다.
// 최소 너비 계산(onMeasureMin)도 이 값을 기준으로 하므로 함께 수정해야 한다.
const getSortIconLeft = (charCount: number) => charCount * 10.75 + 15;

const SortingButton = <TData extends object>({ header, charCount }: SortingButtonProps<TData>) => {
  const calculateLeftPosition = () => {
    return getSortIconLeft(charCount);
  };

  const { sort, setSort } = useSort();

  const targetColumn = SORT_COLUMN[header.column.id];

  const sortHandler = () => {
    const columnId = header.column.id;
    startTransition(() => {
      setSort(SORT_COLUMN[columnId as SortColumnId]);
    });
  };

  return (
    <button
      className={cn(
        'items-centertext-gr-400 absolute flex flex-col hover:text-gr-600',
        'top-1/2',
        '-translate-y-1/2',
      )}
      style={{
        left: `${calculateLeftPosition()}px`, // 이 부분만 style로 남겨야 함
      }}
    >
      {targetColumn &&
        (sort?.sortDirection === 'asc' ? (
          <SortAscIcon onClick={sortHandler} />
        ) : sort?.sortDirection === 'desc' ? (
          <SortDescIcon onClick={sortHandler} />
        ) : (
          <SortDefaultIcon onClick={sortHandler} />
        ))}
    </button>
  );
};

export const DraggableTableHeader = <TData extends object>({
  header,
  disableDrag = false,
  enableResize = false,
  onMeasureMin,
}: DraggableTableHeaderProps<TData>) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: header.column.id,
    disabled: disableDrag,
  });

  const isCheckbox = header.column.id === TABLE_CHECKBOX_COLUMN_ID;
  const checkboxColumn = header.column as typeof header.column & {
    getIsChecked?: () => boolean;
  };
  const isChecked = checkboxColumn.getIsChecked?.() ?? false;
  const sortableTransform = CSS.Translate.toString(transform);
  const stickyTransform = isCheckbox ? 'translateX(var(--table-scroll-left, 0px))' : '';
  const combinedTransform = [stickyTransform, sortableTransform].filter(Boolean).join(' ');
  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isCheckbox ? 'sticky' : 'relative', // 체크박스는 sticky
    left: isCheckbox ? 0 : 'auto', // 왼쪽에 고정
    transform: combinedTransform || undefined,
    transition: 'width transform 0.2s',
    whiteSpace: 'nowrap',
    width: header.column.getSize(),
    zIndex: isCheckbox ? TABLE_CHECKBOX_HEADER_Z_INDEX : isDragging ? 30 : 1,
    // 평상시 체크박스 헤더는 split header 배경색(bg-gr-50)을 className 으로 칠해 가림 처리한다.
    background: isChecked ? '#EAF2FF' : isDragging ? '#F2F0FF' : isCheckbox ? undefined : 'transparent',
  };

  const textRef = useRef<HTMLDivElement>(null);
  const [charCount, setCharCount] = useState(0);

  // 글자 수 계산 + 헤더 최소너비 보고(헤더 텍스트 + 정렬버튼 폭).
  // 주의: textRef 는 flex-1 로 컬럼 폭만큼 늘어나므로 DOM 너비 측정값을 쓰면 폭이 ratchet 된다.
  // 컬럼 폭과 무관한 글자 수 기반 고유값으로 계산한다.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const length = (el.textContent || '').length;
    setCharCount(length);
    if (enableResize && onMeasureMin) {
      // 정렬 컬럼: 아이콘이 getSortIconLeft 위치에 16px 로 그려지므로
      // 아이콘 우측 끝 + 여백 5px 가 최소 너비. (텍스트 폭은 오프셋에 이미 포함)
      // 일반 컬럼: 한글 12px 폰트 기준 ~12px/자 + 좌우 패딩(24).
      const minWidth = header.column.getCanSort()
        ? Math.ceil(getSortIconLeft(length) + 16 + 5)
        : Math.ceil(length * 12) + 24;
      onMeasureMin(header.column.id, minWidth);
    }
  }, [header.column.columnDef.header, enableResize, onMeasureMin, header.column]);

  return (
    <th
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={style}
      className={cn('h-[36px]  text-left Caption1M12', isCheckbox && 'bg-gr-50')}
    >
      <div className="flex min-w-0 items-center">
        {/* 헤더 텍스트 컨테이너. 컬럼이 좁아지면 말줄임(…). */}
        <div
          ref={textRef}
          className="relative min-w-0 flex-1 truncate"
          {...attributes}
          {...listeners}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>

        {/* 정렬 버튼 (글자 수 기반 위치) */}
        {header.column.getCanSort() && <SortingButton header={header} charCount={charCount} />}
      </div>

      {/* 컬럼 너비 조절 핸들. reorder 드래그와 충돌하지 않도록 mousedown 전파를 막는다. */}
      {enableResize && header.column.getCanResize() && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`${header.column.id} 너비 조절`}
          onMouseDown={(event) => {
            event.stopPropagation();
            header.getResizeHandler()(event);
          }}
          onTouchStart={(event) => {
            event.stopPropagation();
            header.getResizeHandler()(event);
          }}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => {
            event.stopPropagation();
            header.column.resetSize();
          }}
          className={cn(
            'absolute right-0 top-0 z-[3] flex h-full w-2 cursor-col-resize touch-none select-none items-center justify-center',
            'after:h-1/2 after:w-px after:bg-gr-300 after:transition-colors',
            'hover:after:h-full hover:after:bg-point-400',
            header.column.getIsResizing() && 'after:h-full after:w-0.5 after:bg-point-500',
          )}
        />
      )}
    </th>
  );
};
